/**
 * The one place that answers "what does a reference tag look like for this
 * route?".
 *
 * Why it exists: the tag written into an outbound prompt used to be a literal,
 * duplicated in `videoReferencePlan.js` and `videoReferenceBindings.js`, each
 * with its own copy of the prefix map. A route could declare
 * `transport.promptTagFormat`, but that field never reached either builder — it
 * only decided whether the model got role vocabulary. So every model was sent
 * Seedance's syntax whatever it declared.
 *
 * That is not cosmetic. Providers document different syntax and bind on it:
 * Seedance's fal schema says "Refer to them in the prompt as @Image1, @Image2",
 * while MiniMax H3's says "Image 1, Image 2". Sending the wrong one uploads the
 * media and then fails to associate it with anything in the prompt — a silent
 * wrong result, not an error.
 */

/** Directive-tag formats. `{Media}` is Image/Video/Audio, `{index}` is 1-based. */
export const MEDIA_PROMPT_TAG_FORMATS = Object.freeze({
  /** Seedance: `@Image1`. */
  AT_PREFIXED: '@{Media}{index}',
  /** MiniMax H3: `Image 1`. */
  SPACED: '{Media} {index}',
  /** Gemini Omni: `<IMAGE_REF_0>`. Zero-based, and image-only by construction. */
  INDEXED_IMAGE: '<IMAGE_REF_{index0}>',
});

const MEDIA_TAG_LABEL = Object.freeze({
  image: 'Image',
  video: 'Video',
  audio: 'Audio',
});

/**
 * ⚠️ RENDERING AND ROLE CAPABILITY ARE DIFFERENT QUESTIONS, and this file is where
 * conflating them does damage.
 *
 * `DIRECTIVE_TAG_FORMATS` answers "does this dialect compile a reference's ROLE
 * into a prompt sentence", which is what `deriveRolesByMedia` gates on. The
 * indexed dialect is deliberately NOT in it: Gemini's route offers no role
 * vocabulary, and widening this set to include it renames the reference CHIP
 * LABELS a user sees from `Image 1` to `Subject 1`. That was tried, measured,
 * and reverted -- it is a product decision about the role picker, not a
 * consequence of how a provider addresses its media.
 *
 * `RENDERABLE_TAG_FORMATS` answers "do I know how to write this dialect down".
 * Every declared format belongs here, because a format we cannot render silently
 * falls back to Seedance's `@Image1` -- which is the exact silent-wrong-result
 * this module exists to prevent, and which Gemini was still getting.
 */
const DIRECTIVE_TAG_FORMATS = new Set([
  MEDIA_PROMPT_TAG_FORMATS.AT_PREFIXED,
  MEDIA_PROMPT_TAG_FORMATS.SPACED,
]);

const RENDERABLE_TAG_FORMATS = new Set(Object.values(MEDIA_PROMPT_TAG_FORMATS));

export function isRenderableMediaPromptTagFormat(format) {
  return RENDERABLE_TAG_FORMATS.has(format);
}

export const DEFAULT_MEDIA_PROMPT_TAG_FORMAT = MEDIA_PROMPT_TAG_FORMATS.AT_PREFIXED;

/**
 * Whether a format compiles references into prompt sentences, which is what
 * makes a transport able to carry semantic ROLE rather than bare position.
 *
 * Role capability must key on this rather than on one specific literal.
 * Comparing against a single format silently stripped the role vocabulary from
 * any model that addressed its references differently.
 */
export function isDirectiveMediaPromptTagFormat(format) {
  return DIRECTIVE_TAG_FORMATS.has(format);
}

/**
 * Render one reference tag. Unknown formats fall back to the default rather
 * than emitting a broken literal, because a tag is written into a paid request.
 */
export function renderMediaPromptTag(format, mediaType, ordinal) {
  const label = MEDIA_TAG_LABEL[mediaType];
  if (!label) return null;

  const template = isRenderableMediaPromptTagFormat(format)
    ? format
    : DEFAULT_MEDIA_PROMPT_TAG_FORMAT;

  // `{index0}` first: a zero-based dialect must not have `{index}` matched
  // inside it.
  return template
    .replace('{Media}', label)
    .replace('{index0}', String(ordinal - 1))
    .replace('{index}', String(ordinal));
}

/** The format a route declares, or the default when it declares none. */
export function resolveRouteMediaPromptTagFormat(route) {
  const declared = route?.references?.transport?.promptTagFormat;
  return isRenderableMediaPromptTagFormat(declared)
    ? declared
    : DEFAULT_MEDIA_PROMPT_TAG_FORMAT;
}

/**
 * Stamp each reference entry with its ordinal within its media type, and the
 * tag the provider will be told to address it by.
 *
 * Lives here rather than in the reference plan because both halves are this
 * module's subject: the ordinal is what the tag is rendered from, and the
 * dialect is the route's. The plan owns which references exist and in what
 * order; how they are NAMED is not its fact.
 */
export function assignReferenceTags(entries, tagFormat) {
  const counts = { image: 0, video: 0, audio: 0 };
  return entries.map((entry) => {
    const ordinal = counts[entry.mediaType] + 1;
    counts[entry.mediaType] = ordinal;
    return {
      ...entry,
      ordinal,
      tag: renderMediaPromptTag(tagFormat, entry.mediaType, ordinal),
    };
  });
}
